import { createSlice, createSelector, createAsyncThunk } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';

const CLARITY_ORDER = ["FL", "IF", "VVS1", "VVS2", "VS1", "VS2", "SI1", "SI2", "I1"];

// Tags are derived rather than a field on the diamond, so they need a sentinel
// sort key that the sort comparator resolves via getHighlights().
export const TAGS_SORT_KEY = '__tags';

function deriveCaratRange(carats) {
  const ct = parseFloat(carats);
  if (isNaN(ct)) return '';
  if (ct < 0.40) return "0.30-0.39";
  if (ct < 0.50) return "0.40-0.49";
  if (ct < 0.60) return "0.50-0.59";
  if (ct < 0.70) return "0.60-0.69";
  if (ct < 0.80) return "0.70-0.79";
  if (ct < 0.90) return "0.80-0.89";
  if (ct < 1.00) return "0.90-0.99";
  if (ct < 1.10) return "1.00-1.09";
  if (ct < 1.20) return "1.10-1.19";
  if (ct < 1.30) return "1.20-1.29";
  if (ct < 1.40) return "1.30-1.39";
  if (ct < 1.50) return "1.40-1.49";
  if (ct < 1.70) return "1.50-1.69";
  if (ct < 1.90) return "1.70-1.89";
  if (ct < 2.00) return "1.90-1.99";
  if (ct < 2.25) return "2.00-2.24";
  if (ct < 2.50) return "2.25-2.49";
  if (ct < 3.00) return "2.50-2.99";
  return "3.00+";
}

function normalizeShape(s) {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function titleCase(s) {
  if (!s) return s;
  return s.trim().replace(/\b\w/g, c => c.toUpperCase());
}

// Normalize raw API diamonds: fix case, derive carat range, extract pre-filled offers/feedback
function processApiDiamonds(rawDiamonds) {
  const counterOffers = {};
  const feedback = {};

  const diamonds = rawDiamonds.map(d => {
    const ldId = String(d['LD ID']);
    const normalized = {
      ...d,
      Shape:    normalizeShape(d.Shape),
      Color:    (d.Color   || '').toUpperCase(),
      Clarity:  (d.Clarity || '').toUpperCase(),
      Cut:      (d.Cut     || '').toUpperCase(),
      Polish:   (d.Polish  || '').toUpperCase(),
      Sym:      (d.Sym     || '').toUpperCase(),
      Fluor:    (d.Fluor   || '').toUpperCase(),
      Lab:      (d.Lab     || '').toUpperCase(),
      Shade:    titleCase(d.Shade || ''),
      Milky:    titleCase(d.Milky || ''),
      Location: titleCase(d.Location || d.location || ''),
      bucket:   (d.bucket || '').toUpperCase(),
      'Carat Range': d['Carat Range'] || deriveCaratRange(d.Carats),
    };

    // Pre-populate counter offers from the API's existing offer values
    const offerRap   = d['Offer Rap']   != null ? String(d['Offer Rap'])   : '';
    const offerPPC   = d['Offer PPC']   != null ? String(d['Offer PPC'])   : '';
    const offerTotal = d['Offer Total'] != null ? String(d['Offer Total']) : '';
    if (offerRap || offerPPC || offerTotal) {
      counterOffers[ldId] = { offerRap, offerPPC, offerTotal };
    }

    // Feedback intentionally NOT pre-populated — textarea starts empty,
    // past values shown via the PastComments tooltip on the card.

    return normalized;
  });

  return { diamonds, counterOffers, feedback };
}

// ── Offer vs current price ────────────────────────────────────────────────────
// An offer typed this session (counterOffers) wins over whatever the API pre-filled
// on the stone, so the filter reacts to what the user is actually looking at.
function getOffer(diamond, counterOffers = {}) {
  const session = counterOffers[String(diamond['LD ID'])];
  if (session) return session;
  return {
    offerRap:   diamond['Offer Rap'],
    offerPPC:   diamond['Offer PPC'],
    offerTotal: diamond['Offer Total'],
  };
}

const isNum = (v) => v !== '' && v != null && isFinite(parseFloat(v));

// Compared at 2dp because calcOffer stores offers via toFixed(2), so an offer written
// at the asking price will not be bit-identical to the stone's raw price.
function sameTo2dp(a, b) {
  if (!isNum(a) || !isNum(b)) return false;
  return Math.round(parseFloat(a) * 100) === Math.round(parseFloat(b) * 100);
}

// True when the stone has an offer and it lands on the current asking price. Any one
// of the three pairs matching counts: the three offer values are derived from each
// other, so rounding can leave one or two of them a hair off.
export function offerMatchesCurrentPrice(diamond, counterOffers) {
  const { offerRap, offerPPC, offerTotal } = getOffer(diamond, counterOffers);
  if (![offerRap, offerPPC, offerTotal].some(isNum)) return false;
  return (
    sameTo2dp(offerRap,   diamond['Rap %']) ||
    sameTo2dp(offerPPC,   diamond['$/Ct']) ||
    sameTo2dp(offerTotal, diamond['Total Price'])
  );
}

// ── Async thunks ──────────────────────────────────────────────────────────────
export const fetchSelectionDiamonds = createAsyncThunk(
  'selection/fetchDiamonds',
  async (_, { rejectWithValue }) => {
    try {
      const token = Cookies.get('access_token');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/gn_reco_selection`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'ngrok-skip-browser-warning': 'true',
          },
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      return json.data || [];
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Direct call to Google Apps Script web app — no backend proxy needed.
// Content-Type is intentionally omitted so the browser sends a simple request
// (text/plain by default for string body), which skips CORS preflight.
// GAS web apps don't return CORS headers for POST, so we use no-cors.
// The request still goes through and GAS writes to the sheet —
// we just can't read the response, so we do an optimistic update.
const callGAS = async (payload) => {
  const url = "https://script.google.com/macros/s/AKfycbxk-HCgd_uatlPesr4N3mu8P56m2ffYSAOHzj8IJd7TjNGkx13pkyeMlve0oRUha2dA7w/exec";
  if (!url) throw new Error('REACT_APP_SELECTION_GAS_URL is not set in .env');
  await fetch(url, {
    method: 'POST',
    mode: 'no-cors',
    body: JSON.stringify(payload),
  });
};

export const approveSelection = createAsyncThunk(
  'selection/approve',
  async (payload, { rejectWithValue }) => {
    try {
      await callGAS({ action: 'approve', ...payload });
      return { ldId: payload.ldId };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const rejectSelection = createAsyncThunk(
  'selection/reject',
  async (payload, { rejectWithValue }) => {
    try {
      await callGAS({ action: 'reject', ...payload });
      return { ldId: payload.ldId, certNo: payload.certNo };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// ── Initial state ─────────────────────────────────────────────────────────────
const INITIAL_FILTERS = {
  search: '',
  shapes: [],
  colors: [],
  clarities: [],
  suppliers: [],
  users: [],
  caratRanges: [],
  demands: [],
  locations: [],
  buckets: [],
  tags: [],
  // Boolean toggle: keep only stones whose offer equals the current asking price.
  offerMatchesPrice: false,
  // Range filters:
  // needsMin/Max       → need_2x
  // grpNeedsMin/Max    → grp_need_2x
  // invCountMin/Max    → inv
  // salesCountMin/Max  → sales_1y
  needsMin: '', needsMax: '',
  grpNeedsMin: '', grpNeedsMax: '',
  invCountMin: '', invCountMax: '',
  grpInvCountMin: '', grpInvCountMax: '',
  salesCountMin: '', salesCountMax: '',
  grpSalesCountMin: '', grpSalesCountMax: '',
  ytdSalesCountMin: '', ytdSalesCountMax: '',
  d90SalesCountMin: '', d90SalesCountMax: '',
  grpYtdSalesCountMin: '', grpYtdSalesCountMax: '',
  grp90DSalesCountMin: '', grp90DSalesCountMax: '',
};

const selectionSlice = createSlice({
  name: 'selection',
  initialState: {
    diamonds: [],
    isLoading: false,
    error: null,
    filters: INITIAL_FILTERS,
    view: 'card',
    page: 1,
    pageSize: 15,
    sort: { column: null, order: 'asc' },
    counterOffers: {},
    feedback: {},
    sidebarSort: { col: 'name', dir: 'asc' },
    filterPanelOpen: true,
    activeSupplierFilter: null,
    reviewStatuses: JSON.parse(localStorage.getItem('sel_review_statuses') || '{}'),

  },
  reducers: {
    setFilter(state, action) {
      const { key, value } = action.payload;
      state.filters[key] = value;
      state.page = 1;
    },
    setAllFilters(state, action) {
      state.filters = { ...INITIAL_FILTERS, ...action.payload, search: state.filters.search };
      state.page = 1;
    },
    clearFilters(state) {
      state.filters = INITIAL_FILTERS;
      state.page = 1;
      state.activeSupplierFilter = null;
    },
    setView(state, action) {
      state.view = action.payload;
    },
    setPage(state, action) {
      state.page = action.payload;
    },
    setPageSize(state, action) {
      state.pageSize = parseInt(action.payload, 10);
      state.page = 1;
    },
    toggleSort(state, action) {
      const column = action.payload;
      if (state.sort.column === column) {
        state.sort.order = state.sort.order === 'asc' ? 'desc' : 'asc';
      } else {
        state.sort.column = column;
        state.sort.order = 'asc';
      }
      state.page = 1;
    },
    setCounterOffer(state, action) {
      const { ldId, offerRap, offerPPC, offerTotal } = action.payload;
      state.counterOffers[String(ldId)] = { offerRap, offerPPC, offerTotal };
    },
    setFeedback(state, action) {
      const { ldId, value } = action.payload;
      state.feedback[String(ldId)] = value;
    },
    toggleSidebarSort(state, action) {
      const col = action.payload;
      if (state.sidebarSort.col === col) {
        state.sidebarSort.dir = state.sidebarSort.dir === 'asc' ? 'desc' : 'asc';
      } else {
        state.sidebarSort.col = col;
        state.sidebarSort.dir = 'asc';
      }
    },
    toggleFilterPanel(state) {
      state.filterPanelOpen = !state.filterPanelOpen;
    },
    // setDiamondStatus(state, action) {
    //   const { ldId, status } = action.payload;
    //   const idx = state.diamonds.findIndex(d => String(d['LD ID']) === String(ldId));
    //   if (idx !== -1) state.diamonds[idx].Status = status;
    // },

    setDiamondStatus(state, action) {
      const { ldId, status } = action.payload;
      const idx = state.diamonds.findIndex(d => String(d['LD ID']) === String(ldId));
      if (idx !== -1) state.diamonds[idx].Status = status;

      // Persist
      const stored = JSON.parse(localStorage.getItem('sel_statuses') || '{}');
      stored[String(ldId)] = status;
      localStorage.setItem('sel_statuses', JSON.stringify(stored));
    },

    setReviewStatus(state, action) {
      const { ldId, status } = action.payload;
      state.reviewStatuses[String(ldId)] = status;

      // Persist to localStorage
      const stored = JSON.parse(localStorage.getItem('sel_review_statuses') || '{}');
      stored[String(ldId)] = status;
      localStorage.setItem('sel_review_statuses', JSON.stringify(stored));
    },
    toggleSupplierFilter(state, action) {
      const supplier = action.payload;
      if (state.activeSupplierFilter === supplier) {
        // Deselect: clear sidebar filter entirely
        state.activeSupplierFilter = null;
        state.filters.suppliers = [];
      } else {
        // Select: replace suppliers filter with only this one
        state.activeSupplierFilter = supplier;
        state.filters.suppliers = [supplier];
      }
      state.page = 1;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchSelectionDiamonds.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSelectionDiamonds.fulfilled, (state, action) => {
        state.isLoading = false;
        const { diamonds, counterOffers, feedback } = processApiDiamonds(action.payload);

        //old
        // state.diamonds = diamonds;
        // Only set pre-filled values that haven't been manually edited this session
        // Object.entries(counterOffers).forEach(([id, offer]) => {
        //   if (!state.counterOffers[id]) state.counterOffers[id] = offer;
        // });
        // Object.entries(feedback).forEach(([id, val]) => {
        //   if (!state.feedback[id]) state.feedback[id] = val;
        // });
        // Rehydrate statuses from localStorage
        const stored = JSON.parse(localStorage.getItem('sel_statuses') || '{}');
        state.diamonds = diamonds.map(d => ({
          ...d,
          Status: stored[String(d['LD ID'])] || d.Status || '',
        }))
      })
      .addCase(fetchSelectionDiamonds.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to load selection data';
      })
      // approve → update status in local state
      .addCase(approveSelection.fulfilled, (state, action) => {
        const { ldId } = action.payload;
        const idx = state.diamonds.findIndex(d => String(d['LD ID']) === String(ldId));
        if (idx !== -1) state.diamonds[idx].Status = 'Shortlisted';
      })
      // reject → remove all diamonds with same cert_no from local state
      .addCase(rejectSelection.fulfilled, (state, action) => {
        const { certNo } = action.payload;
        state.diamonds = state.diamonds.filter(
          d => String(d['Cert No'] || '') !== String(certNo)
        );
      });
  },
});

export const {
  setFilter, setAllFilters, clearFilters,
  setView, setPage, setPageSize, toggleSort,
  setCounterOffer, setFeedback, setDiamondStatus,
  toggleSidebarSort, toggleFilterPanel, toggleSupplierFilter,setReviewStatus,
} = selectionSlice.actions;

export default selectionSlice.reducer;

// ── Selectors ─────────────────────────────────────────────────────────────────

export const selectFilteredDiamonds = createSelector(
  [
    state => state.selection.diamonds,
    state => state.selection.filters,
    state => state.selection.sort,
    state => state.selection.counterOffers,
  ],
  (diamonds, filters, sort, counterOffers) => {
    const {
    shapes, colors, clarities, suppliers, caratRanges, demands, locations, buckets,
    needsMin, needsMax, grpNeedsMin, grpNeedsMax,
    invCountMin, invCountMax, grpInvCountMin, grpInvCountMax,  // ← add these
    salesCountMin, salesCountMax, grpSalesCountMin, grpSalesCountMax, grpYtdSalesCountMin, grpYtdSalesCountMax,
    grp90DSalesCountMin, grp90DSalesCountMax, search,
    ytdSalesCountMin, ytdSalesCountMax, d90SalesCountMin, d90SalesCountMax, users,
    tags = [], offerMatchesPrice = false,
  } = filters;

    const toF = v => parseFloat(v);
    const hasVal = v => v !== '' && !isNaN(toF(v));

    let filtered = diamonds.filter(d => {
      const loc = d.Location || d.location || '';
      if (shapes.length     && !shapes.includes(d.Shape))              return false;
      if (colors.length     && !colors.includes(d.Color))              return false;
      if (clarities.length  && !clarities.includes(d.Clarity))         return false;
      if (suppliers.length  && !suppliers.includes(d.Supplier))        return false;
      if (caratRanges.length && !caratRanges.includes(d['Carat Range'])) return false;
      if (demands.length    && !demands.includes(d['Demand Name']))     return false;
      if (locations.length  && !locations.includes(loc))               return false;
      if (buckets.length    && !buckets.includes(d.bucket))            return false;
      if (users.length && !users.includes(d.User))                     return false;

      // Tags are derived, not a field — a stone matches if it carries ANY selected tag.
      if (tags.length) {
        const stoneTags = getHighlights(d).map(h => h.label);
        if (!tags.some(t => stoneTags.includes(t))) return false;
      }

      if (offerMatchesPrice && !offerMatchesCurrentPrice(d, counterOffers)) return false;

      // need_2x (individual)
      const needsV = toF(d.need_2x ?? 0);
      if (hasVal(needsMin) && needsV < toF(needsMin)) return false;
      if (hasVal(needsMax) && needsV > toF(needsMax)) return false;

      // grp_need_2x
      const grpV = toF(d.grp_need_2x ?? 0);
      if (hasVal(grpNeedsMin) && grpV < toF(grpNeedsMin)) return false;
      if (hasVal(grpNeedsMax) && grpV > toF(grpNeedsMax)) return false;

      // inv (individual)
      const invV = toF(d.inv ?? 0);
      if (hasVal(invCountMin) && invV < toF(invCountMin)) return false;
      if (hasVal(invCountMax) && invV > toF(invCountMax)) return false;

      // grp_inv (grouped)
      const grpInvV = toF(d.grp_inv ?? 0);   // ← use whatever your field name is
      if (hasVal(grpInvCountMin) && grpInvV < toF(grpInvCountMin)) return false;
      if (hasVal(grpInvCountMax) && grpInvV > toF(grpInvCountMax)) return false;


      // sales_1y (individual)
      const salesV = toF(d.sales_1y ?? 0);
      if (hasVal(salesCountMin) && salesV < toF(salesCountMin)) return false;
      if (hasVal(salesCountMax) && salesV > toF(salesCountMax)) return false;


     // sales_1y (grouped)
      const grpsalesV = toF(d.grp_sales_1y ?? 0);   // ← fix
      if (hasVal(grpSalesCountMin) && grpsalesV < toF(grpSalesCountMin)) return false;
      if (hasVal(grpSalesCountMax) && grpsalesV > toF(grpSalesCountMax)) return false;

      // sales ytd (individual)
      const salesYtdV = toF(d.sales_ytd ?? 0);       // ← fix
      if (hasVal(ytdSalesCountMin) && salesYtdV < toF(ytdSalesCountMin)) return false;
      if (hasVal(ytdSalesCountMax) && salesYtdV > toF(ytdSalesCountMax)) return false;

      // sales ytd (grouped)
      const grpSalesYtdV = toF(d.grp_sales_ytd ?? 0); // ← fix
      if (hasVal(grpYtdSalesCountMin) && grpSalesYtdV < toF(grpYtdSalesCountMin)) return false;
      if (hasVal(grpYtdSalesCountMax) && grpSalesYtdV > toF(grpYtdSalesCountMax)) return false;

      // sales 90 days (individual)
      const d90SalesV = toF(d.sales_90d ?? 0);        // ← fix
      if (hasVal(d90SalesCountMin) && d90SalesV < toF(d90SalesCountMin)) return false;
      if (hasVal(d90SalesCountMax) && d90SalesV > toF(d90SalesCountMax)) return false;

      // sales 90 days (grouped)
      const sales90DV = toF(d.grp_sales_90d ?? 0);    // ← fix
      if (hasVal(grp90DSalesCountMin) && sales90DV < toF(grp90DSalesCountMin)) return false;
      if (hasVal(grp90DSalesCountMax) && sales90DV > toF(grp90DSalesCountMax)) return false;

      if (search) {
        const terms = search.toLowerCase().split(/[\s,]+/).map(t => t.trim()).filter(Boolean);
        const ldId    = d['LD ID']    ? d['LD ID'].toString()            : '';
        const stockNo = (d['Stock No'] || '').toString().toLowerCase();
        const certNo  = (d['Cert No']  || '').toString().toLowerCase();
        if (terms.length > 1 || (terms.length === 1 && /^[\w-]+$/.test(terms[0]) && terms[0].length >= 3)) {
          if (!terms.includes(ldId) && !terms.some(t => stockNo === t) && !terms.some(t => certNo === t)) return false;
        } else {
          const allValues = Object.values(d).join(' ').toLowerCase();
          if (!allValues.includes(search)) return false;
        }
      }

      return true;
    });

    if (sort.column) {
      const mul = sort.order === 'asc' ? 1 : -1;
      const valueFor = (d) => {
        // Tags: sort on the canonical label list so identical tag sets cluster.
        if (sort.column === TAGS_SORT_KEY) {
          return getHighlights(d).map(h => h.label).join(', ');
        }
        if (sort.column === 'Clarity') return CLARITY_ORDER.indexOf(d.Clarity);
        return d[sort.column] ?? '';
      };
      filtered = [...filtered].sort((a, b) => {
        const vA = valueFor(a);
        const vB = valueFor(b);
        // '' is not NaN, so blanks fall through as 0 in the numeric branch — which is
        // what we want for the count columns (no inventory sorts as none).
        if (!isNaN(vA) && !isNaN(vB)) return (parseFloat(vA) - parseFloat(vB)) * mul;
        return String(vA).localeCompare(String(vB)) * mul;
      });
    }

    return filtered;
  }
);

export const selectFilterOptions = createSelector(
  [state => state.selection.diamonds],
  (diamonds) => ({
    shapes:     [...new Set(diamonds.map(d => d.Shape))].filter(Boolean).sort(),
    colors:     [...new Set(diamonds.map(d => d.Color))].filter(Boolean).sort(),
    clarities:  [...new Set(diamonds.map(d => d.Clarity))].filter(Boolean)
      .sort((a, b) => CLARITY_ORDER.indexOf(a) - CLARITY_ORDER.indexOf(b)),
    suppliers:  [...new Set(diamonds.map(d => d.Supplier))].filter(Boolean).sort(),
    caratRanges:[...new Set(diamonds.map(d => d['Carat Range']))].filter(Boolean)
      .sort((a, b) => parseFloat(a) - parseFloat(b)),
    demands:    [...new Set(diamonds.map(d => d['Demand Name']))].filter(Boolean).sort(),
    locations:  [...new Set(diamonds.map(d => d.Location || d.location || ''))].filter(Boolean).sort(),
    buckets:    [...new Set(diamonds.map(d => d.bucket))].filter(Boolean).sort(),
    users: [...new Set(diamonds.map(d => d.User))].filter(Boolean).sort(),
    tags: (() => {
      const present = new Set();
      diamonds.forEach(d => getHighlights(d).forEach(h => present.add(h.label)));
      return HIGHLIGHT_TAG_LABELS.filter(t => present.has(t));
    })(),
  })
);

export const selectSupplierStats = createSelector(
  [selectFilteredDiamonds, state => state.selection.sidebarSort],
  (diamonds, sidebarSort) => {
    const map = {};
    diamonds.forEach(d => {
      const sup = d.Supplier || 'Unknown';
      if (!map[sup]) map[sup] = { count: 0, total: 0 };
      map[sup].count++;
      map[sup].total += parseFloat(d['Total Price'] || 0);
    });
    const { col, dir } = sidebarSort;
    return Object.entries(map)
      .sort(([nameA, a], [nameB, b]) => {
        let diff;
        if (col === 'count') diff = a.count - b.count;
        else if (col === 'total') diff = a.total - b.total;
        else diff = nameA.localeCompare(nameB);
        return dir === 'asc' ? diff : -diff;
      })
      .map(([name, stats]) => ({ name, ...stats }));
  }
);

// Canonical order for the Tags filter. `label` is the stable name a tag is filtered
// by; `text` is the per-stone rendering, which may embed a count and so is unusable
// as a filter option.
export const HIGHLIGHT_TAG_LABELS = ['Need', 'Grp Need', 'Large Stone', 'Not In Inv'];

export function getHighlights(diamond) {
  const highlights = [];
  const needsInd = parseFloat(diamond.need_2x ?? 0);
  const needsGrp = parseFloat(diamond.grp_need_2x ?? 0);
  const invInd   = diamond.inv != null ? parseInt(diamond.inv) : null;

  if (needsInd > 0)  highlights.push({ label: 'Need', type: 'good', text: `Need ×${needsInd}` });
  if (needsGrp > 0 && needsInd <= 0) highlights.push({ label: 'Grp Need', type: 'good', text: `Grp Need ×${needsGrp}` });
  if (invInd === 0)  highlights.push({ label: 'Not In Inv', type: 'bad',  text: 'Not In Inv' });
  if (parseFloat(diamond.Carats) > 3) highlights.push({ label: 'Large Stone', type: 'good', text: 'Large Stone' });

  return highlights;
}
